import { RootState } from "@base/store";
import Divider from "@components/atoms/divider";
import ExitToAppIcon from "@mui/icons-material/ExitToApp";
import {
  Alert,
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  Typography,
} from "@mui/material";
import MuiButton from "@mui/material/Button";
import Link from "@mui/material/Link";
import dayjs from "dayjs";
import { ReactElement, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";

import {
  ActionWrapper,
  AddToCalendarBtnWrapper,
  BtnWrapper,
  CardTexts,
  CardTitle,
  Container,
  Content,
  CurrencyWrapper,
  DetailFooterWrapper,
  EditIconWrapper,
  FeesAndCurrencyWrapper,
  FeesInfoWrapper,
  Footer,
  FooterWrapper,
  GameDetailTypography,
  Header,
  IconWrapper,
  Info,
  InfoCardButton,
  InfoWrapper,
  ShareIconWrapper,
  TitleWrapper,
  Wrapper,
} from "./gameDetailsCardMini.styled";
import { MiniComponentProps } from "./gameDetailsCardMini.type";

import { ReactComponent as Calendar } from "@assets/images/calender/calendar.svg";
import { ReactComponent as Cash } from "@assets/images/cash/BsCash.svg";
import { ReactComponent as Chat } from "@assets/images/chat/chat-bubble-left-ellipsis.svg";
import { ReactComponent as ChevronDown } from "@assets/images/chevrons/chevron-down.svg";
import { ReactComponent as ChevronUp } from "@assets/images/chevrons/chevron-up.svg";
import { ReactComponent as Control } from "@assets/images/control/control.svg";
import { ReactComponent as USD } from "@assets/images/currencies/BsCurrencyDollar.svg";
import { ReactComponent as EUR } from "@assets/images/currencies/BsCurrencyEuro.svg";
import { ReactComponent as GBP } from "@assets/images/currencies/BsCurrencyPound.svg";
import { ReactComponent as AED } from "@assets/images/currencies/trade 1.svg";
import { ReactComponent as Pencil } from "@assets/images/edit/pencil.svg";
import { ReactComponent as PinMap } from "@assets/images/pin-map/map-pin.svg";
import { ReactComponent as Share } from "@assets/images/share/share.svg";
import { ReactComponent as Check } from "@assets/images/status-icons/check.svg";
import { ReactComponent as WhiteCheck } from "@assets/images/status-icons/white-check.svg";
import { ReactComponent as Cross } from "@assets/images/status-icons/x-mark.svg";
import { ReactComponent as WhiteCross } from "@assets/images/status-icons/x-white-mark.svg";

const GameDetailsCardMini = ({
  date,
  fee,
  location,
  title,
  addressCoordinate,
  startDate,
  currency,
}: MiniComponentProps) => {
  const navigate = useNavigate();
  function shortenUrlToLength(originalUrl: string, length: number) {
    if (originalUrl.length <= length) {
      return originalUrl;
    }

    // Extract the protocol and domain
    const url = new URL(originalUrl);
    const protocolDomain = url.protocol + "//" + url.host;

    // Calculate the available space for the path and query
    const availableSpace = length - protocolDomain.length - 3; // 3 for "..."

    // Get the path and query
    let pathQuery = url.pathname + url.search;

    // Shorten the path and query if necessary
    if (pathQuery.length > availableSpace) {
      pathQuery = pathQuery.slice(0, availableSpace) + "...";
    }

    // Reconstruct the shortened URL
    const shortenedUrl = protocolDomain + pathQuery;

    return shortenedUrl;
  }

  const InfoRender = (
    icon: ReactElement,
    title?: string | number,
    isLink?: boolean,
    link?: string
  ) => {
    return (
      <Info>
        <Box>{icon}</Box>
        {isLink ? (
          <Link
            sx={{ wordWrap: "break-word", cursor: "pointer" }}
            onClick={() => {
              window.open(link, "_blank");
            }}
          >
            {shortenUrlToLength(link!, 30)}
          </Link>
        ) : (
          <Typography
            sx={{
              wordWrap: "break-word",
              fontFamily: "NotoSans-Medium",
              fontSize: "14px",
            }}
          >
            {title}
          </Typography>
        )}
      </Info>
    );
  };

  const mapInfoRender = (
    icon: ReactElement,
    title?: string | number,
    isLink?: boolean,
    link?: string
  ) => {
    return (
      <Info>
        <Box>{icon}</Box>
        {isLink ? (
          <Link
            sx={{ wordWrap: "break-word", cursor: "pointer" }}
            onClick={() => {
              window.open(link, "_blank");
            }}
          >
            {title}
          </Link>
        ) : (
          <Typography
            sx={{
              wordWrap: "break-word",
              fontFamily: "NotoSans-Medium",
              fontSize: "14px",
            }}
          >
            {title}
          </Typography>
        )}
      </Info>
    );
  };

  const CurrencyPriceRender = (icon: ReactElement, title: string | number) => {
    return (
      <Info>
        <Box>{icon}</Box>
        <CurrencyWrapper isPound={currency === "GBP"}>
          <Typography sx={{ wordWrap: "break-word" }}>{title}</Typography>
          {currencyRender(currency)}
        </CurrencyWrapper>
      </Info>
    );
  };

  const currencyRender = (currency: string) => {
    switch (currency) {
      case "USD":
        return <USD width={15} height={15} />;
      case "AED":
        return <AED width={15} height={15} />;
      case "GBP":
        return <GBP width={15} height={15} />;
      case "EUR":
        return <EUR width={15} height={15} />;
      default:
        break;
    }
  };

  const getDate = (date: string) => {
    return new Date(date).toISOString().slice(0, 10);
  };

  const getTime = (d: string) => {
    const date = new Date(d).toISOString();
    const timeObject = dayjs(date);
    const formattedTime = timeObject.format("HH:mm");
    return formattedTime;
  };

  return (
    <Container>
      <Wrapper>
        <Header>
          <TitleWrapper>
            <CardTitle>{title}</CardTitle>
          </TitleWrapper>
        </Header>
        <Content>
          {InfoRender(<Calendar />, date)}
          {mapInfoRender(
            <PinMap />,
            location,
            !!addressCoordinate?.latitude,
            `https://www.google.com/maps?q=${addressCoordinate?.latitude},${addressCoordinate?.longitude}`
          )}
          <FeesInfoWrapper>
            <FeesAndCurrencyWrapper>
              {CurrencyPriceRender(<Cash />, fee)}
            </FeesAndCurrencyWrapper>
          </FeesInfoWrapper>
        </Content>
      </Wrapper>
    </Container>
  );
};

export default GameDetailsCardMini;
